import React from 'react';
import ServerError from '../../../../components/ServerError';
import { useQueryParams } from '../../../../hooks/useQueryParams';
import {
  OrganizationLoginOptionType,
  useGetAuthenticationOptionsQuery,
} from '../../__generated__/graphql';
import { useTranslation } from 'react-i18next';
import Loading from './Loading';
import LoginPasswordForm from './LoginPasswordForm';
import LoginWithApiToken from './LoginWithApiToken';
import LoginWithSaml from './LoginWithSaml';
import LoginPasswordFormJwt from './LoginPasswordFormJwt';
import LoginWithAuthorizationCode from './LoginWithAuthorizationCode';

const LoginPage: React.FC = () => {
  const { data, loading, error } = useGetAuthenticationOptionsQuery();
  const { t } = useTranslation();

  if (loading) {
    return <Loading />;
  }

  if (error || !data) {
    return <ServerError />;
  }

  const renderPasswordForm = () => {
    if (data.getAuthenticationOptions?.jwt?.enabled) {
      return <LoginPasswordFormJwt />;
    }
    return <LoginPasswordForm />;
  };

  if ((data.getAuthenticationOptions?.options ?? []).length > 1) {
    const hasPassword = data.getAuthenticationOptions?.options?.some(
      (option) => option.type === OrganizationLoginOptionType.Password,
    );

    return (
      <>
        {hasPassword && (
          <div className="mb-3">
            {renderPasswordForm()}
            <p className="text-muted text-center">{t('signIn.or')}</p>
          </div>
        )}
        {!hasPassword && (
          <>
            <h1 className="display-4 text-center mb-3">{t('signIn.title')}</h1>
            <p className="text-muted text-center mb-5">
              {t('signInSaml.multipleProviders')}
            </p>
          </>
        )}
        {data.getAuthenticationOptions?.options?.map((option) => {
          if (option.type === OrganizationLoginOptionType.Saml) {
            return (
              <LoginWithSaml
                key={option.name}
                provider={option.name}
                headless
                className="mb-3"
              />
            );
          }
          return null;
        })}
      </>
    );
  }

  if (data.getAuthenticationOptions?.saml) {
    return (
      <LoginWithSaml provider={data.getAuthenticationOptions?.saml.name} />
    );
  }

  return renderPasswordForm();
};

const Login: React.FC = () => {
  const {
    apiToken,
    authorizationCode,
    jwt: jwtServerSideFeatureEnabled,
  } = useQueryParams();

  if (authorizationCode) {
    return <LoginWithAuthorizationCode authorizationCode={authorizationCode} />;
  }

  if (apiToken) {
    return <LoginWithApiToken apiToken={apiToken} />;
  }

  // for jwt beta testing from client side
  const devMode = import.meta.env.MODE !== 'production';
  if (jwtServerSideFeatureEnabled || devMode) {
    return <LoginPasswordFormJwt />;
  }

  return <LoginPage />;
};

export default Login;
